/**
 * PortfolioDeliverabilityPanel — cross-brand anomaly roll-up.
 *
 * Calls /api/portfolio/deliverability-alerts which iterates every brand
 * in the org, runs the same anomaly detector each BrandDashboard banner
 * uses, and aggregates fired alerts. Answers "which of my 12 brands
 * has a deliverability problem RIGHT NOW" without opening 12 dashboards.
 *
 * Renders nothing when zero brands have fired alerts (silent absence,
 * matches the per-brand banner's behavior).
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { usePagination } from "@/lib/usePagination";
import { Pager } from "@/components/ui/Pager";
import type { DeliverabilityAlert, PortfolioDeliverabilityBrand } from "@/lib/types";

const SEVERITY_TINT: Record<string, { bg: string; fg: string; ring: string; Icon: typeof AlertTriangle }> = {
  critical: {
    bg: "bg-priority-critical-bg",
    fg: "text-priority-critical",
    ring: "ring-priority-critical/30",
    Icon: AlertCircle,
  },
  warning: {
    bg: "bg-platform-amazon-bg",
    fg: "text-platform-amazon",
    ring: "ring-platform-amazon/30",
    Icon: AlertTriangle,
  },
};

export function PortfolioDeliverabilityPanel() {
  const query = useQuery({
    queryKey: ["portfolio", "deliverability-alerts"],
    queryFn: api.getPortfolioDeliverabilityAlerts,
  });

  const pg = usePagination(query.data?.items ?? []);

  if (
    !query.data ||
    query.data.total_brands_with_alerts === 0
  ) {
    return null;
  }

  const { total_brands_with_alerts, total_brands_checked, total_alerts } = query.data;

  return (
    <section
      aria-label="Cross-brand deliverability alerts"
      className="rounded-2xl bg-bg-card shadow-mx overflow-hidden"
    >
      <header className="px-4 py-3 border-b border-border-lighter">
        <div className="flex items-center gap-2">
          <AlertCircle size={14} className="text-priority-critical" />
          <h2 className="text-sm font-semibold text-text-primary">
            Deliverability alerts
          </h2>
          <span className="text-[10px] text-text-dim ml-auto tabular-nums">
            {total_alerts} alert{total_alerts === 1 ? "" : "s"} ·{" "}
            {total_brands_with_alerts} of {total_brands_checked} brand
            {total_brands_checked === 1 ? "" : "s"}
          </span>
        </div>
        <p className="text-xs text-text-muted mt-0.5">
          Bounce / unsub / spam rates above each brand's own 30d baseline.
          Click a brand to see the per-day series on its Customers page.
        </p>
      </header>
      <ul className="divide-y divide-border-lighter">
        {pg.pageItems.map((row) => (
          <BrandRow key={row.brand.id} row={row} />
        ))}
      </ul>
      <div className="px-4 pb-2">
        <Pager p={pg} noun="brands" />
      </div>
    </section>
  );
}

function BrandRow({ row }: { row: PortfolioDeliverabilityBrand }) {
  const tint = SEVERITY_TINT[row.highest_severity] ?? SEVERITY_TINT.warning;
  const { Icon } = tint;
  return (
    <li>
      <Link
        to={`/brand/${row.brand.slug}/customers`}
        className="block px-4 py-2.5 hover:bg-bg-hover transition-colors group"
      >
        <div className="flex items-center gap-3">
          <Icon size={14} className={cn("shrink-0", tint.fg)} />
          <span className="text-sm font-semibold text-text-primary shrink-0 min-w-[140px]">
            {row.brand.name}
          </span>
          <div className="flex-1 min-w-0 flex flex-wrap gap-1.5">
            {row.alerts.map((a) => (
              <AlertChip key={a.metric} alert={a} />
            ))}
          </div>
          <ArrowRight
            size={12}
            className="text-text-dim group-hover:text-accent shrink-0"
          />
        </div>
      </Link>
    </li>
  );
}

function AlertChip({ alert }: { alert: DeliverabilityAlert }) {
  const tint = SEVERITY_TINT[alert.severity] ?? SEVERITY_TINT.warning;
  const ratio = `${alert.ratio_vs_baseline.toFixed(1)}×`;
  return (
    <span
      className={cn(
        "inline-flex items-baseline gap-1 px-2 py-0.5 rounded text-[11px] tabular-nums",
        tint.bg,
        tint.fg,
      )}
      title={`${alert.metric_label} on ${alert.channel}s: 7d median ${alert.recent_pct.toFixed(2)}% vs baseline ${alert.baseline_pct.toFixed(2)}% (${ratio})`}
    >
      <span className="font-semibold">{alert.metric_label}</span>
      <span className="opacity-75">·</span>
      <span>{alert.channel}s</span>
      <span>{alert.recent_pct.toFixed(2)}%</span>
      <span className="text-[10px] opacity-75">({ratio})</span>
    </span>
  );
}
